/**
 * Clients — phase 2 §7.2.
 *
 * "**Clients and Organizations are two views of one table**, and the split is by
 * audience rather than by filter alone. Production teams want active projects,
 * account lead, and next deliverable; the sales side wants relationship types,
 * owner, and last touch. Different default columns and sort, same rows.
 * Production never opens Organizations, which is how the CRM stops being noise
 * for people who do not need it."
 *
 * So this is not Organizations with a dropdown pre-set. It asks the API for the
 * open `client` relationship (§3.2 makes that a row, so the filter is a fact
 * rather than a guess) and renders the production columns: the projects running
 * under each client, and the space the work lives in. What it does *not* show is
 * the relationship column, which is the whole point — a production lead does not
 * need to be told that a client is a client.
 *
 * The projects come from the projects list, one query for all of them, grouped
 * by organization here. That is a presentation of a set the API already
 * resolved, never a second resolution (§7.5): a project missing from this page
 * is a project the person cannot reach, which is the correct answer and the same
 * one `/projects` gives.
 */

import { Link } from '@tanstack/react-router';
import { useOrganizations, useProjects } from '../../api/queries.ts';
import { formatDate } from '../../components/format.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { ErrorState } from '../../components/ui/feedback.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';

export function ClientListPage() {
  const clients = useOrganizations('client');
  const projects = useProjects({});

  const byOrganization = new Map<string, Array<{ id: string; name: string; status: string }>>();
  for (const project of projects.data?.projects ?? []) {
    const list = byOrganization.get(project.organizationId) ?? [];
    list.push({ id: project.id, name: project.name, status: project.status });
    byOrganization.set(project.organizationId, list);
  }

  return (
    <>
      <PageHeader
        title="Clients"
        description="Organizations Matchbox is currently delivering for, and the work running under each. Same rows as Organizations, read for a different job — this one drops the relationship column, because everybody here is a client."
      />

      <Card padded={false}>
        {clients.isError ? (
          <div className="p-5">
            <ErrorState error={clients.error} />
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Client</TH>
                <TH>Projects</TH>
                <TH>Space</TH>
                <TH>Client since</TH>
              </TR>
            </THead>
            <TBody>
              {clients.isPending ? (
                <TableEmpty colSpan={4}>Loading…</TableEmpty>
              ) : clients.data.organizations.length === 0 ? (
                <TableEmpty colSpan={4}>
                  No client you can reach. An organization becomes a client by holding an open
                  client relationship, which is what mints its space pair (§3.3) — prospects and
                  vendors live on the Organizations page.
                </TableEmpty>
              ) : (
                clients.data.organizations.map((org) => {
                  const running = byOrganization.get(org.id) ?? [];
                  const since = org.relationships.find(
                    (row) => row.type === 'client' && row.endedOn === null,
                  );

                  return (
                    <TR key={org.id} className="hover:bg-secondary">
                      <TD>
                        <Link
                          to="/organizations/$organizationId"
                          params={{ organizationId: org.id }}
                          className="font-medium text-primary no-underline hover:underline"
                        >
                          {org.name}
                        </Link>
                      </TD>
                      <TD>
                        {running.length === 0 ? (
                          <span className="text-sm text-tertiary">None you can reach</span>
                        ) : (
                          <span className="flex flex-wrap items-center gap-2">
                            {running.map((project) => (
                              <Link
                                key={project.id}
                                to="/projects/$projectId"
                                params={{ projectId: project.id }}
                                className="text-sm link"
                              >
                                {project.name}
                              </Link>
                            ))}
                          </span>
                        )}
                      </TD>
                      <TD>
                        {org.space ? (
                          <Link
                            to="/spaces/$spaceId"
                            params={{ spaceId: org.space.id }}
                            className="link"
                          >
                            {org.space.name}
                          </Link>
                        ) : (
                          // §3.3 makes this unreachable for a client, and it is
                          // rendered rather than assumed away: an organization
                          // holding the relationship and no space is a broken
                          // invariant, and a dash is how it would show up here.
                          <Badge tone="gray">None</Badge>
                        )}
                      </TD>
                      <TD className="whitespace-nowrap">
                        {since ? formatDate(since.startedOn) : '—'}
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}
